import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

export interface ClientApiAdminClient {
  client_uuid: string;
  name: string;
  brand_id: number;
  so_user_id: number | null;
  environment_mode: 'sandbox' | 'live';
  sync_enabled: boolean;
  status: 'pending' | 'active' | 'disabled';
  storage_mode: string;
  active_secret_count: number;
  scopes: string[];
  created_at: string;
  updated_at: string;
  access_blocked: boolean;
  access_block_scope: 'global' | 'brand' | null;
  access_block_reason: string | null;
  live_test_enabled: boolean;
}

export interface ClientApiAccessControl {
  scope_type: 'global' | 'brand';
  scope_key: number;
  blocked: boolean;
  reason: string | null;
  updated_by_user_id: number | null;
  request_id: string | null;
  updated_at: string | null;
}

export interface ClientApiAccessControlState {
  global: ClientApiAccessControl;
  brand: ClientApiAccessControl | null;
  effective_blocked: boolean;
  effective_scope: 'global' | 'brand' | null;
  effective_reason: string | null;
}

export interface ClientApiAdminClientPayload {
  name: string;
  brand_id: number;
  so_user_id: number | null;
  environment_mode: string;
  sync_enabled: boolean;
  status: string;
  scopes: string[];
}

export interface ClientApiDocsLinks {
  client_api_base_url: string;
  docs_url: string;
  openapi_url: string;
  guide_url?: string;
  client_uuid?: string;
  client_docs_url?: string;
  client_openapi_url?: string;
  client_guide_url?: string;
  docs_key_configured: boolean;
}

export interface ClientApiPlaygroundLaunch {
  client_uuid: string;
  client_name: string;
  brand_id: number;
  environment: 'local' | 'backvault' | 'production';
  mode: 'playground' | 'live';
  launch_url: string;
  expires_in: number;
  expires_at: string;
}

export interface ClientApiLiveTestControl {
  brand_id: number;
  enabled: boolean;
  reason: string | null;
  updated_by_user_id: number | null;
  request_id: string | null;
  updated_at: string | null;
}

export interface ClientApiAdminRuntime {
  service: string;
  environment: string;
  version: string;
  base_url: string;
  database_name: string;
  live_sync_enabled: boolean;
  so_lookups_enabled: boolean;
  so_log_sync_enabled: boolean;
  so_trigger_automation_enabled: boolean;
  trigger_driver: string;
  so_api_base_url: string;
  document_staging_configured: boolean;
  document_max_upload_mb: number;
  document_allowed_extensions: string;
}

@Injectable({
  providedIn: 'root'
})
export class ClientApiAdminService {
  private readonly clientApiEnvironment = environment as any;

  readonly localBaseUrl = this.clientApiEnvironment.clientApiLocalURL || 'http://localhost:8088';
  readonly backvaultBaseUrl = this.clientApiEnvironment.clientApiBackvaultURL || 'https://client-api.backvault.com.au';
  readonly productionBaseUrl = this.clientApiEnvironment.clientApiProductionURL || 'https://client-api.sharmangroup.au';
  readonly localPortalUrlTemplate = this.clientApiEnvironment.clientApiLocalPortalURLTemplate || 'https://localhost:44362/#/client/profile/{brand_id}';
  readonly backvaultPortalUrlTemplate = this.clientApiEnvironment.clientApiBackvaultPortalURLTemplate || 'https://tsa.backvault.com.au/#/client/profile/{brand_id}';
  readonly productionPortalUrlTemplate = this.clientApiEnvironment.clientApiProductionPortalURLTemplate || 'https://sharmans.online/#/client/profile/{brand_id}';
  readonly localSampleAppUrl = this.clientApiEnvironment.clientApiLocalSampleAppURL || 'http://127.0.0.1:8090';
  readonly backvaultSampleAppUrl = this.clientApiEnvironment.clientApiBackvaultSampleAppURL || 'https://backvault-my-investigations-corp.sharmangroup.au';
  readonly productionSampleAppUrl = this.clientApiEnvironment.clientApiProductionSampleAppURL || 'https://my-investigations-corp.sharmangroup.au';
  readonly sampleSourceUrl = this.clientApiEnvironment.clientApiSampleSourceURL || '';
  readonly brokerBaseUrl = this.clientApiEnvironment.apiURL || this.clientApiEnvironment.$api_get_url;

  selectedBaseUrl = this.resolveRuntimeBaseUrl();

  constructor(private http: HttpClient) {}

  listClients(baseUrl: string, limit = 100, brandId?: number): Observable<any> {
    let params = new HttpParams().set('limit', String(limit));
    if (brandId && brandId > 0) {
      params = params.set('brand_id', String(brandId));
    }

    return this.http.get<any>(this.brokerUrl('/clients'), {
      params
    });
  }

  status(): Observable<{ data: ClientApiAdminRuntime }> {
    return this.operations(1).pipe(
      map(response => ({
        ...(response || {}),
        data: this.runtimeFromOperations(response)
      }))
    );
  }

  docsLinks(clientUuid?: string): Observable<{ data: ClientApiDocsLinks }> {
    let params = new HttpParams();
    if (clientUuid) {
      params = params.set('client_uuid', clientUuid);
    }
    return this.http.get<any>(this.brokerUrl('/docs-links'), { params });
  }

  operations(limit = 25): Observable<any> {
    const params = new HttpParams().set('limit', String(limit));
    return this.http.get<any>(this.brokerUrl('/operations'), { params }).pipe(
      map(response => {
        this.runtimeFromOperations(response);
        return response;
      })
    );
  }

  runtimeFromOperations(response: any): ClientApiAdminRuntime {
    const runtime = response && response.data ? response.data.runtime : null;
    const requiredBooleanFields = [
      'live_sync_enabled',
      'so_lookups_enabled',
      'so_log_sync_enabled',
      'so_trigger_automation_enabled'
    ];

    if (!runtime
      || !runtime.environment
      || requiredBooleanFields.some(field => typeof runtime[field] !== 'boolean')) {
      throw new Error('Client API operations did not return a complete operational runtime status.');
    }

    return runtime as ClientApiAdminRuntime;
  }

  accessControls(brandId?: number): Observable<{ data: ClientApiAccessControlState }> {
    let params = new HttpParams();
    if (brandId && brandId > 0) {
      params = params.set('brand_id', String(brandId));
    }
    return this.http.get<any>(this.brokerUrl('/access-controls'), { params });
  }

  setGlobalAccessControl(blocked: boolean, reason: string): Observable<{ data: ClientApiAccessControlState }> {
    return this.http.put<any>(this.brokerUrl('/access-controls/global'), { blocked, reason });
  }

  setBrandAccessControl(brandId: number, blocked: boolean, reason: string): Observable<{ data: ClientApiAccessControlState }> {
    return this.http.put<any>(
      this.brokerUrl('/access-controls/brands/' + encodeURIComponent(String(brandId))),
      { blocked, reason }
    );
  }

  createClient(baseUrl: string, payload: ClientApiAdminClientPayload): Observable<any> {
    return this.http.post<any>(this.brokerUrl('/clients'), payload);
  }

  updateClient(baseUrl: string, clientUuid: string, payload: ClientApiAdminClientPayload): Observable<any> {
    return this.http.put<any>(
      this.brokerUrl('/clients/' + encodeURIComponent(clientUuid)),
      payload
    );
  }

  rotateSecret(baseUrl: string, clientUuid: string, deactivateExisting = true, revokeTokens = false): Observable<any> {
    return this.http.post<any>(
      this.brokerUrl('/clients/' + encodeURIComponent(clientUuid) + '/secrets'),
      { label: 'angular-admin-rotation', deactivate_existing: deactivateExisting, revoke_tokens: revokeTokens }
    );
  }

  playgroundLaunch(clientUuid: string, brandId?: number): Observable<{ data: ClientApiPlaygroundLaunch }> {
    return this.http.post<any>(
      this.brokerUrl('/clients/' + encodeURIComponent(clientUuid) + '/playground-launch'),
      brandId && brandId > 0 ? { brand_id: brandId } : {}
    );
  }

  liveTestControl(brandId: number): Observable<{ data: ClientApiLiveTestControl }> {
    return this.http.get<any>(
      this.brokerUrl('/live-test/brands/' + encodeURIComponent(String(brandId)))
    );
  }

  setLiveTestControl(brandId: number, enabled: boolean, reason: string): Observable<{ data: ClientApiLiveTestControl }> {
    return this.http.put<any>(
      this.brokerUrl('/live-test/brands/' + encodeURIComponent(String(brandId))),
      { enabled, reason }
    );
  }

  liveTestLaunch(clientUuid: string, brandId: number): Observable<{ data: ClientApiPlaygroundLaunch }> {
    return this.http.post<any>(
      this.brokerUrl('/clients/' + encodeURIComponent(clientUuid) + '/live-test-launch'),
      { brand_id: brandId }
    );
  }

  activity(baseUrl: string, clientUuid: string, limit = 25): Observable<any> {
    const params = new HttpParams().set('limit', String(limit));
    return this.http.get<any>(
      this.brokerUrl('/clients/' + encodeURIComponent(clientUuid) + '/activity'),
      { params }
    );
  }

  docsUrlFor(baseUrl: string): string {
    return this.appendDocsKey(this.cleanBaseUrl(baseUrl) + '/admin/docs', baseUrl);
  }

  openApiUrlFor(baseUrl: string): string {
    return this.appendDocsKey(this.cleanBaseUrl(baseUrl) + '/admin/openapi.yaml', baseUrl);
  }

  clientSwaggerUrlFor(baseUrl: string, clientUuid: string): string {
    const url = this.cleanBaseUrl(baseUrl) + '/docs?client_uuid=' + encodeURIComponent(clientUuid);
    return this.appendDocsKey(url, baseUrl);
  }

  portalUrlForBrand(brandId: number, baseUrl: string = this.selectedBaseUrl): string {
    const environmentName = this.environmentForBaseUrl(baseUrl);
    const template = environmentName === 'backvault'
      ? this.backvaultPortalUrlTemplate
      : (environmentName === 'production' ? this.productionPortalUrlTemplate : this.localPortalUrlTemplate);
    return template.replace('{brand_id}', encodeURIComponent(String(Number(brandId) || 0)));
  }

  sampleAppUrlFor(baseUrl: string = this.selectedBaseUrl): string {
    const environmentName = this.environmentForBaseUrl(baseUrl);
    return environmentName === 'backvault'
      ? this.backvaultSampleAppUrl
      : (environmentName === 'production' ? this.productionSampleAppUrl : this.localSampleAppUrl);
  }

  private appendDocsKey(url: string, baseUrl: string): string {
    if (this.cleanBaseUrl(baseUrl) !== this.cleanBaseUrl(this.localBaseUrl)) {
      return url;
    }

    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'docs_key=local-docs-key';
  }

  private cleanBaseUrl(baseUrl: string): string {
    return (baseUrl || this.selectedBaseUrl || this.localBaseUrl).replace(/\/+$/, '');
  }

  private environmentForBaseUrl(baseUrl: string): 'local' | 'backvault' | 'production' {
    const selected = this.cleanBaseUrl(baseUrl);
    if (selected === this.cleanBaseUrl(this.backvaultBaseUrl)) {
      return 'backvault';
    }
    if (selected === this.cleanBaseUrl(this.productionBaseUrl)) {
      return 'production';
    }
    if (selected === this.cleanBaseUrl(this.localBaseUrl)) {
      return 'local';
    }

    const hostname = typeof window !== 'undefined' && window.location
      ? window.location.hostname.toLowerCase()
      : '';
    if (hostname === 'backvault.com.au' || hostname.endsWith('.backvault.com.au')) {
      return 'backvault';
    }
    if (
      hostname === 'sharmans.online' || hostname.endsWith('.sharmans.online') ||
      hostname === 'sharmangroup.au' || hostname.endsWith('.sharmangroup.au') ||
      hostname === 'sharmans.net.au' || hostname.endsWith('.sharmans.net.au')
    ) {
      return 'production';
    }
    return 'local';
  }

  private resolveRuntimeBaseUrl(): string {
    const hostname = typeof window !== 'undefined' && window.location
      ? window.location.hostname.toLowerCase()
      : '';
    const configuredBaseUrl = this.clientApiEnvironment.clientApiURL || '';

    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return configuredBaseUrl || this.localBaseUrl;
    }

    if (hostname === 'backvault.com.au' || hostname.endsWith('.backvault.com.au')) {
      return this.backvaultBaseUrl;
    }

    if (
      hostname === 'sharmans.online' || hostname.endsWith('.sharmans.online') ||
      hostname === 'sharmangroup.au' || hostname.endsWith('.sharmangroup.au') ||
      hostname === 'sharmans.net.au' || hostname.endsWith('.sharmans.net.au')
    ) {
      return this.productionBaseUrl;
    }

    return configuredBaseUrl || (environment.production ? this.productionBaseUrl : this.localBaseUrl);
  }

  private brokerUrl(path: string): string {
    return (this.brokerBaseUrl || '').replace(/\/+$/, '') + '/api/client-api/admin' + path;
  }
}
